import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Tabs, Row, Col, Statistic, Button, Tag, Modal, Select, Input,
  Timeline, notification, Spin, Empty, Space, DatePicker, Drawer,
} from 'antd';
import {
  ClockCircleOutlined, CoffeeOutlined, LoginOutlined, LogoutOutlined,
  PlusOutlined, CheckCircleOutlined, LoadingOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import * as svc from './staffTrackingService';
import './StaffTracking.css';
import { getCurrentStaffId } from '../../authHelper';
import AddManualActivity from '../sn_work_tracking/AddManualActivity';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const STATUS_COLOR  = { active: 'green', on_break: 'orange', idle: 'volcano', clocked_out: 'default' };
const STATUS_TEXT   = { active: 'Active', on_break: 'On Break', idle: 'Idle', clocked_out: 'Clocked Out' };
const CAL_STATUS    = ['present', 'absent', 'half_day', 'leave', 'holiday'];
const CAL_LABELS    = { present: 'P', absent: 'A', half_day: 'H', leave: 'L', holiday: 'Ho' };
const DAY_NAMES     = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const fmt = (ds) => ds ? new Date(ds).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A';

export default function StaffTrackingHub() {
  const history  = useHistory();
  const staffId  = getCurrentStaffId();

  const [loading, setLoading]   = useState(true);
  const [status, setStatus]     = useState(null);
  const [summary, setSummary]   = useState(null);
  const [breakTypes, setBreakTypes] = useState({});
  const [breakModal, setBreakModal] = useState(false);
  const [selBreak, setSelBreak] = useState('');
  const [breakNote, setBreakNote] = useState('');

  // Timeline tab
  const [tlDate, setTlDate]     = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [tlLoading, setTlLoading] = useState(false);

  // Week tab
  const [weekData, setWeekData] = useState([]);
  const [weekLoading, setWeekLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Calendar tab
  const [calYear, setCalYear]   = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);
  const [calData, setCalData]   = useState([]);
  const [calLoading, setCalLoading] = useState(false);

  const loadMain = useCallback(async (silent = false) => {
    if (!staffId) return;
    if (!silent) setLoading(true);
    try {
      const [stRes, smRes] = await Promise.all([
        svc.getCurrentStatus(staffId),
        svc.getTodaySummary(staffId),
      ]);
      setStatus(stRes.data);
      setSummary(smRes.data);
    } catch (e) {
      notification.error({ message: 'Error', description: e.message || 'Failed to load' });
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => {
    if (!staffId) { setLoading(false); return; }
    loadMain();
    svc.getBreakTypes().then(r => setBreakTypes(r.data)).catch(() => {});
    const iv = setInterval(() => loadMain(true), 60000);
    return () => clearInterval(iv);
  }, [staffId, loadMain]);

  // --- Clock In / Out / Break handlers ---
  const handleClockIn = async () => {
    setLoading(true);
    try {
      await svc.clockIn(staffId);
      notification.success({ message: 'Clocked in successfully!' });
      await loadMain();
    } catch (e) { notification.error({ message: e.message || 'Failed to clock in' }); setLoading(false); }
  };

  const handleClockOut = () =>
    Modal.confirm({
      title: 'Clock Out', content: 'Are you sure you want to clock out?',
      onOk: async () => {
        setLoading(true);
        try {
          const r = await svc.clockOut(staffId);
          notification.success({ message: `Clocked out — ${r.data.total_work_time} worked` });
          await loadMain();
        } catch (e) { notification.error({ message: e.message || 'Failed' }); setLoading(false); }
      },
    });

  const handleStartBreak = async () => {
    if (!selBreak) { notification.warning({ message: 'Select break type' }); return; }
    setLoading(true);
    try {
      await svc.startBreak(staffId, selBreak, breakNote);
      notification.success({ message: 'Break started' });
      setBreakModal(false); setSelBreak(''); setBreakNote('');
      await loadMain();
    } catch (e) { notification.error({ message: e.message || 'Failed' }); setLoading(false); }
  };

  const handleEndBreak = async () => {
    setLoading(true);
    try {
      const r = await svc.endBreak(staffId);
      notification.success({ message: `Break ended — ${r.data.break_duration_formatted}` });
      await loadMain();
    } catch (e) { notification.error({ message: e.message || 'Failed' }); setLoading(false); }
  };

  // --- Timeline tab ---
  const loadTimeline = async (date) => {
    setTlLoading(true);
    try {
      const r = await svc.getTimeline(staffId, date || undefined);
      // API returns { success, data: { date, timeline: [...] } }
      setTimeline(r.data?.timeline || []);
    } catch { setTimeline([]); }
    finally { setTlLoading(false); }
  };

  // --- Week tab ---
  const loadWeek = async () => {
    setWeekLoading(true);
    try {
      const r = await svc.getWeeklySummary(staffId);
      setWeekData(r.data || []);
    } catch { setWeekData([]); }
    finally { setWeekLoading(false); }
  };

  // --- Calendar tab ---
  const loadCalendar = async (y, m) => {
    setCalLoading(true);
    try {
      const r = await svc.getMonthlyCalendar(staffId, y, m);
      setCalData(r.data || []);
    } catch { setCalData([]); }
    finally { setCalLoading(false); }
  };

  const onTabChange = (key) => {
    if (key === 'timeline') loadTimeline(tlDate || null); // always reload with current date
    if (key === 'week'     && weekData.length === 0)  loadWeek();
    if (key === 'calendar' && calData.length === 0)   loadCalendar(calYear, calMonth);
  };

  // --- Render calendar grid ---
  const renderCalendar = () => {
    const daysInMonth = new Date(calYear, calMonth, 0).getDate();
    const firstDay    = new Date(calYear, calMonth - 1, 1).getDay();
    const dataMap     = {};
    const today       = new Date().toISOString().split('T')[0];
    calData.forEach(r => {
      const d = r.date?.split?.('T')?.[0] || r.date;
      dataMap[d] = r;
    });

    const cells = [];
    // blank cells
    for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} className="st-cal-cell empty" />);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${calYear}-${String(calMonth).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const entry   = dataMap[dateStr];
      const st      = entry?.status || 'absent';
      const hrs     = entry?.total_hours || 0;
      const isToday = dateStr === today;
      cells.push(
        <div key={d} className={`st-cal-cell ${st}${isToday ? ' today' : ''}`}>
          <span className="st-cal-day">{d}</span>
          {hrs > 0 && <span className="st-cal-hrs">{hrs}h</span>}
        </div>
      );
    }

    return (
      <>
        <div className="st-calendar" style={{ marginBottom: 8 }}>
          {DAY_NAMES.map(n => <div key={n} className="st-cal-header">{n}</div>)}
        </div>
        <div className="st-calendar">{cells}</div>
        <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          {CAL_STATUS.map(s => (
            <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
              <span style={{ width: 14, height: 14, borderRadius: 3, display: 'inline-block' }} className={`st-cal-cell ${s}`} />
              {s.replace('_', ' ')}
            </span>
          ))}
        </div>
      </>
    );
  };

  // --- Render week bar chart ---
  const renderWeek = () => {
    const maxHrs = Math.max(...weekData.map(d => parseFloat(d.total_hours) || 0), 1);
    return (
      <div className="st-week-chart">
        {weekData.map((d, i) => {
          const hrs  = parseFloat(d.total_hours) || 0;
          const pct  = Math.round((hrs / maxHrs) * 100);
          const day  = moment(d.date).format('ddd');
          return (
            <div key={i} className="st-week-bar-wrap">
              <span className="st-week-hrs">{hrs > 0 ? `${hrs}h` : '-'}</span>
              <div className={`st-week-bar ${d.status}`} style={{ height: `${Math.max(pct, 4)}%` }} />
              <span className="st-week-day">{day}</span>
            </div>
          );
        })}
      </div>
    );
  };

  if (!staffId) return <Card><Empty description="Could not identify staff. Please log in again." /></Card>;
  if (loading && !status) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  const isClockedOut = status?.current_status === 'clocked_out';
  const isOnBreak    = status?.current_status === 'on_break';

  return (
    <div className="st-hub">
      <Tabs defaultActiveKey="today" onChange={onTabChange} type="card">

        {/* ── TODAY ── */}
        <TabPane tab="Today" key="today">
          <Card className="st-status-card" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={8}>
                <Space direction="vertical" size="small">
                  <h3 style={{ margin: 0 }}>
                    {loading && <LoadingOutlined style={{ marginRight: 8 }} />}
                    Work Status
                  </h3>
                  <Tag color={STATUS_COLOR[status?.current_status]} style={{ fontSize: 15, padding: '6px 14px' }}>
                    {STATUS_TEXT[status?.current_status] || status?.current_status}
                  </Tag>
                  {status?.clock_in_time && (
                    <div style={{ color: '#666', fontSize: 13 }}>
                      {status.current_status === 'clocked_out' && status.clock_out_time ? (
                        <><div>In: {fmt(status.clock_in_time)}</div><div>Out: {fmt(status.clock_out_time)}</div></>
                      ) : (
                        <div>Clocked in at {fmt(status.clock_in_time)}</div>
                      )}
                    </div>
                  )}
                </Space>
              </Col>

              <Col xs={24} md={8} style={{ textAlign: 'center' }}>
                <Statistic
                  title="Time Worked Today"
                  value={summary?.work_status?.total_work_formatted || status?.time_worked_formatted || '0h 0m'}
                  valueStyle={{ color: '#3f8600', fontSize: 28 }}
                  prefix={<ClockCircleOutlined />}
                />
              </Col>

              <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                {isClockedOut ? (
                  <Button type="primary" size="large" icon={<LoginOutlined />} onClick={handleClockIn} style={{ background: '#52c41a' }}>
                    Clock In
                  </Button>
                ) : (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {isOnBreak ? (
                      <Button type="primary" size="large" icon={<CheckCircleOutlined />} onClick={handleEndBreak} style={{ width: '100%' }}>
                        End Break
                      </Button>
                    ) : (
                      <Button size="large" icon={<CoffeeOutlined />} onClick={() => setBreakModal(true)} style={{ width: '100%' }}>
                        Take Break
                      </Button>
                    )}
                    <Button danger size="large" icon={<LogoutOutlined />} onClick={handleClockOut} style={{ width: '100%' }}>
                      Clock Out
                    </Button>
                  </Space>
                )}
              </Col>
            </Row>
          </Card>

          {!isClockedOut && (
            <Card style={{ marginBottom: 16 }}>
              <Button icon={<PlusOutlined />} onClick={() => setDrawerOpen(true)} size="large"
                style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>
                Add Manual Work Entry
              </Button>
            </Card>
          )}

          <Card title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Today's Summary</span>} headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }} style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={12} md={6}><Statistic title="Activities"     value={summary?.activity_summary?.total_activities || 0} prefix={<CheckCircleOutlined style={{ color: '#1890ff' }} />} /></Col>
              <Col xs={12} md={6}><Statistic title="Break Time"     value={summary?.work_status?.total_break_formatted || `${summary?.breaks?.total_minutes || 0}m`} prefix={<CoffeeOutlined style={{ color: '#fa8c16' }} />} /></Col>
              <Col xs={12} md={6}><Statistic title="Productive"     value={summary?.work_status?.productive_formatted || '0h 0m'} prefix={<ClockCircleOutlined style={{ color: '#52c41a' }} />} /></Col>
              <Col xs={12} md={6}><Statistic title="Breaks Taken"   value={summary?.breaks?.count || 0} /></Col>
            </Row>
          </Card>

          {summary?.timeline?.length > 0 ? (
            <Card title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Recent Activity</span>} headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
              <Timeline>
                {summary.timeline.slice(0, 5).map((a, i) => (
                  <Timeline.Item key={i} color={a.entry_type === 'automatic' ? 'green' : 'blue'}>
                    <strong>{fmt(a.start_time)}</strong>{' '}
                    <Tag color="blue">{a.activity_category}</Tag>
                    {a.entry_type === 'automatic' && <Tag color="green">Auto</Tag>}
                    <div style={{ color: '#666' }}>{a.description}</div>
                    {a.duration_minutes > 0 && <div style={{ color: '#999', fontSize: 12 }}>{a.duration_minutes}m</div>}
                  </Timeline.Item>
                ))}
              </Timeline>
            </Card>
          ) : (
            !isClockedOut && <Card title="Activity Timeline"><Empty description="No activities yet today." /></Card>
          )}
        </TabPane>

        {/* ── TIMELINE ── */}
        <TabPane tab="Timeline" key="timeline">
          <Card
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Activity Timeline</span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <DatePicker
                onChange={(_, ds) => { setTlDate(ds); loadTimeline(ds); }}
                placeholder="Select date"
              />
            }
          >
            {tlLoading ? <Spin /> : (
              timeline.length > 0 ? (
                <Timeline>
                  {timeline.map((a, i) => (
                    <Timeline.Item key={i} color={a.entry_type === 'automatic' ? 'green' : 'blue'}>
                      <strong>{fmt(a.start_time)}</strong>{' '}
                      {a.end_time && <span>→ {fmt(a.end_time)}</span>}{' '}
                      <Tag color="blue">{a.activity_category}</Tag>
                      {a.entry_type === 'manual' && <Tag color="orange">Manual</Tag>}
                      <div style={{ color: '#666' }}>{a.description}</div>
                      {a.duration_minutes > 0 && <div style={{ fontSize: 12, color: '#999' }}>Duration: {a.duration_minutes}m</div>}
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : <Empty description="No activities found." />
            )}
          </Card>
        </TabPane>

        {/* ── THIS WEEK ── */}
        <TabPane tab="This Week" key="week">
          <Card title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Hours Worked — Last 7 Days</span>} headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
            {weekLoading ? <Spin /> : (
              weekData.length > 0 ? renderWeek() : <Empty description="No data available." />
            )}
          </Card>
          {weekData.length > 0 && (
            <Card title="Daily Breakdown" style={{ marginTop: 16 }}>
              {weekData.map((d, i) => (
                <Row key={i} justify="space-between" style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Col>{moment(d.date).format('dddd, DD MMM')}</Col>
                  <Col>
                    <Tag color={d.status === 'present' ? 'green' : d.status === 'absent' ? 'red' : 'orange'}>
                      {d.status}
                    </Tag>
                    <span style={{ marginLeft: 8, fontWeight: 600 }}>{d.total_hours}h</span>
                  </Col>
                </Row>
              ))}
            </Card>
          )}
        </TabPane>

        {/* ── CALENDAR ── */}
        <TabPane tab="Calendar" key="calendar">
          <Card
            title={`Attendance — ${new Date(calYear, calMonth - 1).toLocaleString('default', { month: 'long' })} ${calYear}`}
            extra={
              <DatePicker.MonthPicker
                value={moment(`${calYear}-${String(calMonth).padStart(2,'0')}`, 'YYYY-MM')}
                onChange={(m) => {
                  if (!m) return;
                  const y = m.year(), mo = m.month() + 1;
                  setCalYear(y); setCalMonth(mo); loadCalendar(y, mo);
                }}
              />
            }
          >
            {calLoading ? <Spin /> : renderCalendar()}
          </Card>
        </TabPane>

      </Tabs>

      {/* Add Manual Activity Drawer */}
      <Drawer
        title={<span style={{ fontWeight: 700, color: '#7a0000' }}>Add Manual Work Entry</span>}
        width={600}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
      >
        <AddManualActivity
          onSuccess={() => { setDrawerOpen(false); loadMain(); }}
          onCancel={() => setDrawerOpen(false)}
        />
      </Drawer>

      {/* Break modal */}
      <Modal
        title="Start Break"
        visible={breakModal}
        onOk={handleStartBreak}
        onCancel={() => { setBreakModal(false); setSelBreak(''); setBreakNote(''); }}
        okText="Start Break"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <label>Break Type *</label>
            <Select style={{ width: '100%', marginTop: 8 }} placeholder="Select type" value={selBreak} onChange={setSelBreak} size="large">
              {Object.entries(breakTypes).map(([k, v]) => <Option key={k} value={v}>{v}</Option>)}
            </Select>
          </div>
          <div>
            <label>Notes (Optional)</label>
            <TextArea rows={3} value={breakNote} onChange={e => setBreakNote(e.target.value)} style={{ marginTop: 8 }} />
          </div>
        </Space>
      </Modal>
    </div>
  );
}
